import { useAuth } from "@dl/web-core-js/auth";
import { ResourceClient } from "@dl/web-core-js/api";
import {
  Badge,
  Button,
  Card,
  Dialog,
  Field,
  Input,
  Select,
  TabNotPorted,
  enumOptions,
  useConfirm,
} from "@dl/web-core-js/ui";
import { ExternalLink, GripVertical, Plus, Trash2 } from "lucide-react";
import { useQuery } from "@tanstack/react-query";
import { useMemo, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import { toast } from "sonner";
import {
  IMAGE_TYPES,
  fullImageUrl,
  imageUrl,
  type ImageOrientation,
  type ImageTypeOrderDTO,
  type ProductDTO,
  type ProductImageDTO,
} from "../../api/product";
import type { HttpClient } from "@dl/web-core-js/api";

class ProductImageClient extends ResourceClient<ProductImageDTO> {
  constructor(http: HttpClient) {
    super(http, "/product/image");
  }

  /** Raw body + query-param metadata, per the backend's upload convention. */
  add(productId: number, filename: string, type: string, file: File): Promise<ProductImageDTO> {
    return this.http.request<ProductImageDTO>("/product/image/add", {
      method: "POST",
      raw: file,
      query: { product_id: productId, filename, type },
    });
  }
}

interface StagedFile {
  file: File;
  name: string;
  type: string;
  orientation: ImageOrientation;
}

function orientationOptions(t: (key: string, fallback: string) => string) {
  return [
    { value: "FRONT", label: t("product.images.orientation.front", "Front") },
    { value: "ANGLED", label: t("product.images.orientation.angled", "Angled") },
    { value: "NA", label: t("product.images.orientation.na", "Not set") },
  ];
}

/**
 * Product photography.
 *
 * Grouped by type, in the business order of types (Settings › Products), so a
 * product's images always read white background, lifestyle, … whatever order
 * they were uploaded in. Order inside a group is the data: the cards drag
 * within their group, and the whole grouped order writes into the draft's
 * priorities, persisting with the page Save — so the first image (what every
 * feed and the grid thumbnail use) is the first of the first group. Uploads and
 * deletes are immediate, as in Vaadin.
 */
export function ImagesTab({
  draft,
  onChange,
  readOnly,
  onPersisted,
}: {
  draft: ProductDTO;
  onChange: (next: ProductDTO) => void;
  readOnly: boolean;
  onPersisted: () => void;
}) {
  const { t } = useTranslation();
  const { http } = useAuth();
  const confirm = useConfirm();
  const client = useMemo(() => new ProductImageClient(http), [http]);

  const [staged, setStaged] = useState<StagedFile[]>([]);
  const [uploading, setUploading] = useState(false);
  const [editing, setEditing] = useState<ProductImageDTO | null>(null);
  const input = useRef<HTMLInputElement>(null);
  const dragFrom = useRef<{ type: string; index: number } | null>(null);

  const typeClient = useMemo(() => new ResourceClient<ImageTypeOrderDTO>(http, "/product/image-type-order"), [http]);
  const { data: typeRows } = useQuery({
    queryKey: ["config", "image-type-order"],
    queryFn: () => typeClient.findAll(),
    staleTime: 5 * 60_000,
  });
  const typeOrder = useMemo(() => {
    const ordered = [...(typeRows ?? [])].sort((a, b) => a.sort_order - b.sort_order).map((row) => row.image_type);
    return [...ordered, ...IMAGE_TYPES.filter((type) => !ordered.includes(type))];
  }, [typeRows]);

  /** One group per type in the business order (types without images left out), then any untyped image. */
  const groups = useMemo(() => {
    const byPriority = [...(draft.images ?? [])].sort((a, b) => (a.priority ?? 0) - (b.priority ?? 0));
    const result = typeOrder
      .map((type) => ({ type, images: byPriority.filter((image) => image.image_type === type) }))
      .filter((group) => group.images.length > 0);
    const other = byPriority.filter((image) => !image.image_type || !typeOrder.includes(image.image_type));
    if (other.length > 0) result.push({ type: "", images: other });
    return result;
  }, [draft.images, typeOrder]);
  const images = useMemo(() => groups.flatMap((group) => group.images), [groups]);
  const typeLabel = (type: string) => enumOptions(IMAGE_TYPES).find((option) => option.value === type)?.label ?? type;
  const orientationLabel = (orientation: string) =>
    orientationOptions(t).find((option) => option.value === orientation)?.label ?? orientation;

  const stageFiles = (files: FileList) => {
    const next = [...files].slice(0, 10).map((file) => ({
      file,
      name: file.name.replace(/\.[^.]+$/, ""),
      type: "WHITE_BACKGROUND",
      orientation: "NA" as ImageOrientation,
    }));
    setStaged(next);
  };

  const uploadStaged = async () => {
    setUploading(true);
    try {
      for (const entry of staged) {
        const added = await client.add(draft.id, entry.name, entry.type, entry.file);
        // The upload endpoint takes no orientation; anything but Not set is written right after.
        if (entry.orientation !== "NA") await client.update({ ...added, orientation: entry.orientation });
      }
      setStaged([]);
      onPersisted();
      toast.success(t("product.images.uploaded", "{{count}} image(s) added", { count: staged.length }));
    } catch (error) {
      toast.error(error instanceof Error ? error.message : t("error.generic", "Failed"));
    } finally {
      setUploading(false);
      if (input.current) input.current.value = "";
    }
  };

  const remove = async (image: ProductImageDTO) => {
    const confirmed = await confirm({
      title: t("product.images.delete.title", "Delete this image?"),
      variant: "danger",
    });
    if (!confirmed) return;
    try {
      await client.remove(image.id);
      onPersisted();
      toast.success(t("notification.deleted", "Deleted"));
    } catch (error) {
      toast.error(error instanceof Error ? error.message : t("error.generic", "Failed"));
    }
  };

  /** Moves an image inside its group, then renumbers every image in grouped order. */
  const reorder = (type: string, from: number, to: number) => {
    if (from === to) return;
    const next = groups.map((group) => {
      if (group.type !== type) return group.images;
      const moved = [...group.images];
      const [image] = moved.splice(from, 1);
      moved.splice(to, 0, image!);
      return moved;
    });
    onChange({
      ...draft,
      images: next.flat().map((image, index) => ({ ...image, priority: index })),
    });
  };

  return (
    <div className="flex flex-col gap-3">
      {!readOnly && (
        <div>
          <Button
            size="sm"
            variant="primary"
            icon={<Plus className="size-4" />}
            onClick={() => input.current?.click()}
          >
            {t("product.images.add", "Add images")}
          </Button>
          <input
            ref={input}
            type="file"
            accept="image/jpeg,image/png,image/tiff"
            multiple
            hidden
            onChange={(event) => {
              if (event.target.files?.length) stageFiles(event.target.files);
            }}
          />
        </div>
      )}

      {images.length === 0 ? (
        <TabNotPorted message={t("product.images.none", "This product has no images.")} />
      ) : (
        <div className="flex flex-col gap-4">
          {groups.map((group) => (
            <section key={group.type || "other"} className="flex flex-col gap-2">
              <h3 className="text-[12px] font-medium text-ink-muted">
                {group.type ? typeLabel(group.type) : t("product.images.group.other", "No type")} · {group.images.length}
              </h3>
              <ul className="grid grid-cols-2 gap-3 sm:grid-cols-3 lg:grid-cols-6 2xl:grid-cols-8">
                {group.images.map((image, index) => {
                  // Full resolution on the card: the stored thumbnails are sized for
                  // a 28px grid cell and upscale blurry at card size.
                  const url = fullImageUrl(image);
                  const fallback = imageUrl(image);
                  return (
                    <li
                      key={image.id}
                      draggable={!readOnly}
                      onDragStart={() => (dragFrom.current = { type: group.type, index })}
                      onDragOver={(event) => event.preventDefault()}
                      onDrop={() => {
                        // Only inside a group: the type, not a drag, decides which group an image is in.
                        if (dragFrom.current !== null && dragFrom.current.type === group.type) reorder(group.type, dragFrom.current.index, index);
                        dragFrom.current = null;
                      }}
                      className="group/img relative"
                    >
                      <Card className="overflow-hidden">
                        {url ? (
                          <img
                            src={url}
                            alt=""
                            loading="lazy"
                            // A dead thumbnail retries once with the full image
                            // before giving up on a placeholder.
                            onError={(event) => {
                              const element = event.currentTarget;
                              if (fallback && element.src !== fallback) {
                                element.src = fallback;
                              } else {
                                element.style.visibility = "hidden";
                              }
                            }}
                            className="aspect-square w-full bg-surface-sunk object-contain"
                          />
                        ) : (
                          <div className="aspect-square w-full bg-surface-sunk" />
                        )}
                        <div className="flex items-center gap-1 px-2 py-1.5">
                          {image.id === images[0]?.id && (
                            <Badge variant="accent" size="sm">
                              {t("product.images.primary", "Primary")}
                            </Badge>
                          )}
                          <span className="truncate text-[12px] text-ink-muted">
                            {orientationLabel(image.orientation ?? "NA")}
                          </span>
                        </div>
                      </Card>

                      <span className="absolute right-1.5 top-1.5 hidden gap-1 group-hover/img:flex">
                        {url && (
                          <Button
                            size="sm"
                            variant="secondary"
                            className="size-7 p-0"
                            aria-label={t("product.images.open", "Open full size")}
                            title={t("product.images.open", "Open full size")}
                            onClick={() => window.open(url, "_blank", "noopener")}
                            icon={<ExternalLink className="size-3.5" />}
                          />
                        )}
                      </span>
                      {!readOnly && (
                        <span className="absolute left-1.5 top-1.5 hidden gap-1 group-hover/img:flex">
                          <span className="inline-flex size-7 cursor-grab items-center justify-center rounded-md bg-surface-over/90 text-ink-faint shadow-card">
                            <GripVertical className="size-3.5" />
                          </span>
                          <Button
                            size="sm"
                            variant="secondary"
                            className="size-7 p-0"
                            aria-label={t("button.edit", "Edit")}
                            onClick={() => setEditing(image)}
                          >
                            ✎
                          </Button>
                          <Button
                            size="sm"
                            variant="secondary"
                            className="size-7 p-0 hover:text-risk"
                            aria-label={t("button.delete", "Delete")}
                            onClick={() => void remove(image)}
                            icon={<Trash2 className="size-3.5" />}
                          />
                        </span>
                      )}
                    </li>
                  );
                })}
              </ul>
            </section>
          ))}
        </div>
      )}

      {/* Staging dialog: each file gets a name and a type before anything uploads. */}
      <Dialog
        open={staged.length > 0}
        onClose={() => setStaged([])}
        title={t("product.images.add", "Add images")}
        size="md"
        actions={
          <>
            <Button variant="secondary" onClick={() => setStaged([])} disabled={uploading}>
              {t("button.cancel", "Cancel")}
            </Button>
            <Button
              variant="primary"
              loading={uploading}
              disabled={staged.some((entry) => !entry.name.trim())}
              onClick={() => void uploadStaged()}
            >
              {t("product.images.upload", "Upload {{count}}", { count: staged.length })}
            </Button>
          </>
        }
      >
        <ul className="flex flex-col gap-3">
          {staged.map((entry, index) => (
            <li key={index} className="grid grid-cols-[1fr_11rem_9rem] items-end gap-2">
              <Field label={entry.file.name} htmlFor={`staged-${index}`}>
                <Input
                  id={`staged-${index}`}
                  value={entry.name}
                  invalid={!entry.name.trim()}
                  onChange={(event) =>
                    setStaged(staged.map((item, position) =>
                      position === index ? { ...item, name: event.target.value } : item,
                    ))
                  }
                />
              </Field>
              <Select
                value={entry.type}
                options={enumOptions(IMAGE_TYPES)}
                onChange={(event) =>
                  setStaged(staged.map((item, position) =>
                    position === index ? { ...item, type: event.target.value } : item,
                  ))
                }
              />
              <Select
                value={entry.orientation}
                aria-label={t("product.images.orientation", "Orientation")}
                options={orientationOptions(t)}
                onChange={(event) =>
                  setStaged(staged.map((item, position) =>
                    position === index ? { ...item, orientation: event.target.value as ImageOrientation } : item,
                  ))
                }
              />
            </li>
          ))}
        </ul>
      </Dialog>

      {/* Type and orientation edits ride the draft and commit with Save. */}
      {editing && (
        <Dialog
          open
          onClose={() => setEditing(null)}
          title={t("product.images.edit", "Edit image")}
          size="sm"
          actions={
            <Button variant="primary" onClick={() => setEditing(null)}>
              {t("button.close", "Close")}
            </Button>
          }
        >
          <Field label={t("column.type", "Type")} htmlFor="image-type">
            <Select
              id="image-type"
              value={editing.image_type ?? ""}
              options={enumOptions(IMAGE_TYPES)}
              onChange={(event) => {
                const type = event.target.value;
                setEditing({ ...editing, image_type: type });
                onChange({
                  ...draft,
                  images: (draft.images ?? []).map((image) =>
                    image.id === editing.id ? { ...image, image_type: type } : image,
                  ),
                });
              }}
            />
          </Field>
          <Field label={t("product.images.orientation", "Orientation")} htmlFor="image-orientation">
            <Select
              id="image-orientation"
              value={editing.orientation ?? "NA"}
              options={orientationOptions(t)}
              onChange={(event) => {
                const orientation = event.target.value as ImageOrientation;
                setEditing({ ...editing, orientation });
                onChange({
                  ...draft,
                  images: (draft.images ?? []).map((image) =>
                    image.id === editing.id ? { ...image, orientation } : image,
                  ),
                });
              }}
            />
          </Field>

        </Dialog>
      )}
    </div>
  );
}
